import { FormControl } from '@mui/material';
import { Search } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { MergeEntityCard } from './merge-entity-card';
import type { MergeTargetPickerProps } from './merge-target-picker.types';

/**
 * Search for one entity until a target is picked, then the picked target as a card with a
 * remove button. The caller owns the search query and the picked record.
 */
export const MergeTargetPicker = <RecordT,>({
  label,
  placeholder,
  icon,
  records,
  target,
  toSummary,
  onTargetChange,
  onSearchChange,
  loading = false,
  hasMore = false,
  loadMore,
  searchInputRef,
}: MergeTargetPickerProps<RecordT>) => {
  const triggerId = useId();
  const [query, setQuery] = useState('');

  const options = useMemo<SelectMenuOption[]>(
    () =>
      records.map((record) => {
        const { id, title } = toSummary(record);
        return { value: id, title, testId: 'merge-target-option' };
      }),
    [records, toSummary],
  );

  const changeQuery = useCallback(
    (next: string) => {
      setQuery(next);
      onSearchChange(next);
    },
    [onSearchChange],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && query) {
        changeQuery('');
      }
    },
    [query, changeQuery],
  );

  const handlePick = useCallback(
    (pickedId: string) => {
      const record = records.find((candidate) => toSummary(candidate).id === pickedId);
      if (record) {
        onTargetChange(record);
      }
    },
    [records, toSummary, onTargetChange],
  );

  const handleLoadMore = useCallback(() => {
    void loadMore?.();
  }, [loadMore]);

  const handleRemove = useCallback(() => {
    changeQuery('');
    onTargetChange(null);
  }, [changeQuery, onTargetChange]);

  if (target) {
    return (
      <MergeEntityCard
        label={label}
        icon={icon}
        summary={toSummary(target)}
        onRemove={handleRemove}
        testId="merge-target-card"
      />
    );
  }

  return (
    <FormControl fullWidth data-testid="merge-target-search">
      <FormFieldLabel htmlFor={triggerId}>{label}</FormFieldLabel>
      <SelectMenu
        ref={searchInputRef}
        id={triggerId}
        label={label}
        variant="field"
        menuWidth="trigger"
        options={options}
        value={null}
        onChange={handlePick}
        placeholder={placeholder}
        triggerIcon={<Search size={16} aria-hidden />}
        searchPlaceholder={placeholder}
        searchValue={query}
        onSearchChange={changeQuery}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        loadingMore={loading}
        onOpenChange={handleOpenChange}
      />
    </FormControl>
  );
};
